/*
Copyright 2020 Adobe
All Rights Reserved.

NOTICE: Adobe permits you to use, modify, and distribute this file in
accordance with the terms of the Adobe license agreement accompanying
it. If you have received this file from a source other than Adobe,
then your use, modification, or distribution of it requires the prior
written permission of Adobe.
*/

import config from "@/config";
import { tokenProvider, xCorrelationIdProvider } from "@/shared/hooks";

declare global {
    interface Window {
        AdobeDC: any;
    }
}

class ViewSDKClient {
    readyPromise: Promise<void>;
    url: string;
    fileName: string;
    adobeDCView: any;
    onClose: any;

    constructor(url, fileName, onClose = null) {
        this.readyPromise = new Promise((resolve) => {
            if (window.AdobeDC) {
                resolve();
            } else {
                /* Wait for Adobe Document Services PDF Embed API to be ready */
                document.addEventListener("adobe_dc_view_sdk.ready", () => {
                    resolve();
                });
            }
        });
        this.url = process.env.STORYBOOK_ENV ? "/example.pdf" : url;
        this.fileName = fileName;
        this.adobeDCView = undefined;
        this.onClose = onClose;
    }

    ready() {
        return this.readyPromise;
    }

    previewFile(divId, viewerConfig) {
        // For PROD: Adobe embed api requires a new token if it is using www
        const clientId = location.hostname.includes("www.") ? config.ADOBE_EMBED_WWW_API : config.ADOBE_EMBED_API;

        const configApi = {
            /* Pass your registered client id */
            clientId,
        };
        if (divId) {
            /* Optional only for Light Box embed mode */
            /* Pass the div id in which PDF should be rendered */
            configApi["divId"] = divId;
        }
        /* Initialize the AdobeDC View object */
        this.adobeDCView = new window.AdobeDC.View(configApi);
        this.adobeDCView.registerCallback(
            /* Type of call back */
            window.AdobeDC.View.Enum.CallbackType.EVENT_LISTENER,
            /* call back function */
            (event) => {
                switch (event.type) {
                    case window.AdobeDC.View.Enum.Events.PDF_VIEWER_CLOSE:
                        this.onClose?.();
                        break;
                    case window.AdobeDC.View.Enum.Events.PDF_VIEWER_OPEN:
                    case window.AdobeDC.View.Enum.Events.PDF_VIEWER_READY:
                    case window.AdobeDC.View.Enum.Events.VIEWER_UNMOUNT:
                    default:
                        break;
                }
            },
        );

        /* Invoke the file preview API on Adobe DC View object */
        return this.adobeDCView.previewFile(
            {
                /* Pass information on how to access the file */
                content: {
                    /* Location of file where it is hosted */
                    location: {
                        url: this.url,
                        // If the file URL requires some additional headers, then it can be passed as follows:-
                        headers: [
                            {
                                key: "Authorization",
                                value: `Bearer ${tokenProvider.getToken()}`,
                            },
                            {
                                key: "X-Correlation-ID",
                                value: xCorrelationIdProvider.getXCorrelationId(),
                            },
                        ],
                    },
                },
                /* Pass metadata of file */
                metaData: {
                    /* file name */
                    fileName: this.fileName,
                    /* file ID */
                    // id: "6d07d124-ac85-43b3-a867-36930f502ac6",
                },
            },
            viewerConfig,
        );
    }
}

export default ViewSDKClient;
